/**
 * Integrations Page (Admin > Settings > Integrations)
 *
 * Currently one integration: ICS calendar subscriptions, which let crew add
 * their ProgramRow schedule to Google/Apple/Outlook from their own profile.
 *
 * Available on every plan (de-gated 2026-08-12) and ON by default — the
 * toggle here is the owner's opt-out. Safe as a default because the feed
 * URLs are per-user unguessable tokens: enabling the org exposes nothing
 * until a member actually subscribes.
 */

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useOrganization } from '../../../context/OrganizationContext';
import { getOrganization, updateOrganization } from '../../../api/organizations';
import { getErrorMessage } from '../../../api/client';
import { ToggleRow } from '../../../components/ui/ToggleRow';

export default function Integrations() {
  const { currentOrg, isOrgOwner } = useOrganization();
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchOrganization = useCallback(async () => {
    try {
      const response = await getOrganization();
      setEnabled(response.organization.calendarFeedEnabled ?? true);
      setError(null);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchOrganization();
  }, [fetchOrganization]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const response = await updateOrganization({ calendarFeedEnabled: enabled });
      setEnabled(response.organization.calendarFeedEnabled ?? enabled);
      toast.success('Integration settings saved');
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-rack border-r-transparent" />
        <p className="mt-2 text-gray-600">Loading integrations...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Breadcrumb */}
      <nav className="mb-4">
        <ol className="flex items-center space-x-2 text-sm text-gray-500">
          <li>
            <Link to="/admin/settings" className="hover:text-gray-700">
              Settings
            </Link>
          </li>
          <li>
            <ChevronRightIcon className="w-4 h-4" />
          </li>
          <li className="text-gray-900 font-medium">Integrations</li>
        </ol>
      </nav>

      {/* Page header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Integrations</h1>
        <p className="text-gray-600 mt-1">
          Connect {currentOrg?.orgName || 'your organization'} to the tools your crew
          already use.
        </p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {!isOrgOwner && (
        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex">
            <InfoIcon className="w-5 h-5 text-yellow-500 mt-0.5 mr-3 flex-shrink-0" />
            <div className="text-sm text-yellow-700">
              <p className="font-medium">View Only</p>
              <p className="mt-1">Only organization owners can modify these settings.</p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Calendar Subscriptions</h2>

          <ToggleRow
            title="Allow calendar subscriptions"
            description="Lets crew add this organization's schedule to Google Calendar, Apple Calendar or Outlook from their own profile page. Each person gets their own private link, and it stops working when they leave the organization."
            enabled={enabled}
            onChange={setEnabled}
            disabled={!isOrgOwner}
            last
          />

          {enabled && (
            <div className="rounded-md bg-amber-50 border border-amber-200 p-3">
              <p className="text-sm text-amber-800">
                Subscribed calendars refresh on the calendar provider's own
                schedule; Google typically checks every few hours. Crew should
                still rely on ProgramRow notifications for last-minute changes.
              </p>
            </div>
          )}
        </div>

        {isOrgOwner && (
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {saving ? (
                <>
                  <span className="inline-block animate-spin rounded-full h-4 w-4 border-2 border-white border-r-transparent mr-2" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        )}
      </form>
    </div>
  );
}

// =============================================================================
// Icons
// =============================================================================

function ChevronRightIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  );
}

function InfoIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

